import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueries, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { RootState } from '../../../store/store';
import {
  getCollectionNotes, getGetCollectionNotesQueryKey, getGetLedgerAccountScheduleQueryKey, getGetLedgerAccountTransactionsQueryKey,
  getLedgerAccountSchedule, getLedgerAccountTransactions,
  useGetCreditAssessmentReport, useGetLedgerAccountsByCustomer, useSaveCreditAssessmentReport,
} from '../../../services/loanService';
import { useRecognisedPercent } from '../../../services/collateralSettings';
import { formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';
import { getFundingProduct } from '../../../constants/fundingProducts';
import {
  CONTROL_OUTCOMES, CONTROL_STATUSES, DECISIONS, DECISION_CODES, Decision, Field, OUTCOMES,
  RATINGS, RISK_AREAS, RISK_LEVEL, Rating, SECTIONS, Section, TEMPLATE_VERSION, Values, areaFindings, areaRating,
  cashSurplus, exposureAfter, fillBlanks, flags, netTransaction, overallRating, prefill, recognisedValue,
  securityCover, totalToRepay,
} from './creditAssessmentTemplate';
import { AccountRecords, earlierLoans } from './tirheloHistory';
import { applicationStatements } from '../../../services/statementIq';
import { useStatementIqSettings } from '../../../services/statementIqSettings';
import { BUSY } from './bankStatementAnalysis';
import BankStatementAnalysisPanel from './BankStatementAnalysisPanel';

const money = (n?: number) => (n === undefined ? '—' : n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' }));
const OUTCOME_CLASS: Record<string, string> = {
  'Pass': 'bg-success', 'Pass with Mitigation': 'bg-info text-dark', 'Fail': 'bg-danger', 'Hard Stop': 'bg-dark', 'N/A': 'bg-secondary',
};
const RATING_CLASS: Record<string, string> = {
  Incomplete: 'bg-secondary', High: 'bg-danger', Moderate: 'bg-warning text-dark', Low: 'bg-success', 'N/A': 'bg-light text-dark border',
};

/** What the report tells the assessment form below it: the recommendation is the assessor's (guidelines, point 5) */
export interface ReportRecommendation {
  decision?: Decision;
  riskLevel?: string;
  rationale?: string;
  overall: Rating;
}

interface Props {
  loanApplication: any;
  /** The existing credit score, director reports and scoring result, shown inside Section 3 (guidelines, point 4) */
  externalCredit?: React.ReactNode;
  onRecommendationChange?: (r: ReportRecommendation) => void;
}

/** One calculated (green) line */
const Calc: React.FC<{ label: string; value?: number; signed?: boolean }> = ({ label, value, signed }) => (
  <div className="d-flex justify-content-between">
    <span>{label}</span>
    <strong className={signed && value !== undefined && value < 0 ? 'text-danger' : ''}>{money(value)}</strong>
  </div>
);

/**
 * The Business Loan Credit Assessment Report on the credit team's template of 27 Oct 2026 (finding #5).
 * Earlier steps fill in what they captured; the assessor reviews, corrects and concludes.
 */
const CreditAssessmentTemplateReport: React.FC<Props> = ({ loanApplication, externalCredit, onRecommendationChange }) => {
  const applicationId: string = loanApplication?.id || '';
  const customerNumber: string = loanApplication?.customerNumber || '';
  const user = useSelector((state: RootState) => state.auth.user);
  const assessor = user?.name || user?.username || '';
  const recognisedPercent = useRecognisedPercent();

  const { data: report, isLoading, refetch } = useGetCreditAssessmentReport(applicationId, { query: { enabled: !!applicationId } });
  const { data: accounts } = useGetLedgerAccountsByCustomer({ customerNumber }, { query: { enabled: !!customerNumber, retry: false } });
  const save = useSaveCreditAssessmentReport();

  const [values, setValues] = useState<Values>({});
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(true);
  // Several sections open at once; Section 3 starts open because the credit score it holds is needed to submit
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(['s1', 's3']));
  const isOpen = (key: string) => openSections.has(key);
  const toggle = (key: string) => setOpenSections(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const started = useRef(false);

  // Section 4: each earlier loan's instalments, payments and collection notes
  const earlierIds = useMemo(() => earlierLoans(accounts as any, applicationId).map(a => String(a.accountId)), [accounts, applicationId]);
  const recordQueries = useQueries({
    queries: earlierIds.flatMap(id => [
      { queryKey: getGetLedgerAccountScheduleQueryKey(id), queryFn: () => getLedgerAccountSchedule(id), retry: false },
      { queryKey: getGetLedgerAccountTransactionsQueryKey(id), queryFn: () => getLedgerAccountTransactions(id), retry: false },
      { queryKey: getGetCollectionNotesQueryKey(id, { size: 500 }), queryFn: () => getCollectionNotes(id, { size: 500 }), retry: false },
    ]),
  });
  const recordsReady = recordQueries.every(q => !q.isLoading);
  const recordsKey = recordQueries.map(q => q.dataUpdatedAt).join(',');
  const records = useMemo(() => {
    if (!recordsReady) return undefined;
    const out: Record<string, AccountRecords> = {};
    earlierIds.forEach((id, i) => {
      out[id] = {
        installments: recordQueries[i * 3].data as any,
        transactions: recordQueries[i * 3 + 1].data as any,
        notes: (recordQueries[i * 3 + 2].data as any)?.content,
      };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsReady, recordsKey, earlierIds]);

  // Section 1: the application's bank statements as StatementIQ analysed them; refreshed while any is processing
  const { data: statementSettings } = useStatementIqSettings();
  const { data: statements, refetch: refetchStatements } = useQuery({
    queryKey: ['statementiq', 'application', applicationId],
    queryFn: () => applicationStatements(applicationId),
    enabled: !!applicationId,
    retry: false,
    refetchInterval: q => ((q.state.data || []).some(c => BUSY.includes(c.status)) ? 15000 : false),
  });
  const bankStatements = useMemo(() => Array.from(loanApplication?.documents || [])
    .filter((d: any) => d?.documentType === 'BANK_STATEMENT' && (!d.status || d.status === 'CURRENT')) as any[], [loanApplication]);

  const fresh = useMemo(() => prefill(loanApplication, {
    accounts: accounts as any, recognisedPercent, records, statements,
  }), [loanApplication, accounts, recognisedPercent, records, statements]);

  // The saved report, or a new one filled in from the application. Keyed on content, not object identity,
  // so a refetch that brings back the same data does not reset what is on screen.
  const freshKey = useMemo(() => JSON.stringify(fresh), [fresh]);
  const reportKey = report ? JSON.stringify([report.exists, report.updatedAt, report.content]) : '';
  useEffect(() => {
    if (!report || dirty) return;
    const next = (report.exists ? report.content || {} : fresh) as Values;
    setValues(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    started.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey, freshKey]);

  const set = (key: string, value: string | number | undefined) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const overall = useMemo(() => overallRating(values), [values]);
  const attention = useMemo(() => flags(values), [values]);
  const decision = values['s12.decision'] as Decision | undefined;

  useEffect(() => {
    onRecommendationChange?.({ decision, riskLevel: RISK_LEVEL[overall], rationale: String(values['s12.rationale'] || ''), overall });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, overall, values['s12.rationale']]);

  const onSave = () => {
    save.mutate(
      {
        loanApplicationId: applicationId,
        data: {
          content: { ...values, _template: TEMPLATE_VERSION } as any,
          recommendation: decision ? DECISION_CODES[decision] : undefined,
          overallResult: overall,
          ...(assessor ? { savedBy: assessor } : {}),
        },
      },
      {
        onSuccess: () => { toast.success('Credit assessment report saved'); setDirty(false); refetch(); },
        onError: (e: any) => toast.error(e?.response?.data?.message || 'The report could not be saved'),
      },
    );
  };

  const fillFromLms = () => { setValues(prev => fillBlanks(prev, fresh)); setDirty(true); toast.info('Blank fields filled in from the application'); };

  const input = (key: string, field: Pick<Field, 'kind' | 'options'>, id = `ca-${key}`) => {
    const value = values[key];
    if (field.kind === 'select') {
      return (
        <select id={id} className="form-select form-select-sm" value={(value as string) || ''} onChange={e => set(key, e.target.value || undefined)}>
          <option value="">Select ↓</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.kind === 'longtext') {
      return <textarea id={id} rows={2} className="form-control form-control-sm" value={(value as string) || ''} onChange={e => set(key, e.target.value)} />;
    }
    return (
      <input id={id} type={field.kind === 'money' ? 'number' : 'text'} step={field.kind === 'money' ? '0.01' : undefined}
        className={`form-control form-control-sm ${field.kind === 'money' && value !== undefined && value !== '' && fresh[key] !== undefined && value === fresh[key] ? 'border-success' : ''}`}
        value={value === undefined ? '' : String(value)}
        onChange={e => set(key, field.kind === 'money' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)} />
    );
  };

  const fromLms = (key: string) => fresh[key] !== undefined && values[key] === fresh[key]
    ? <span className="badge bg-light text-success border ms-1" title="Filled in from earlier LMS steps; you can change it">from LMS</span> : null;

  const outcomeButtons = (key: string, options: readonly string[] = OUTCOMES) => (
    <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
      <span className="small fw-semibold">Assessment Outcome ↓</span>
      {options.map(o => (
        <button key={o} type="button" className={`btn btn-sm ${values[key] === o ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => set(key, values[key] === o ? undefined : o)}>{o}</button>
      ))}
    </div>
  );

  const sectionBadge = (s: Section) => {
    const o = s.outcome ? values[`${s.key}.outcome`] as string | undefined : undefined;
    if (!s.outcome) return null;
    return o ? <span className={`badge ${OUTCOME_CLASS[o]} ms-2`}>{o}</span> : <span className="badge bg-light text-muted border ms-2">No outcome</span>;
  };

  const renderSection = (s: Section) => (
    <div className="p-3">
      {s.question && <p className="small text-muted fst-italic mb-3">{s.question}</p>}
      {s.key === 's1' && (
        <BankStatementAnalysisPanel applicationId={applicationId} documents={bankStatements} captures={statements}
          enabled={statementSettings?.enabled} onChanged={() => { refetchStatements(); }} />
      )}
      {s.key === 's3' && externalCredit && <div className="mb-3">{externalCredit}</div>}
      {s.rows && (
        <>
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle mb-0">
              <thead className="small text-muted"><tr><th>Manual subsection</th><th>Control Status ↓</th><th>Key Position</th><th>Assessment Outcome ↓</th><th>Condition / Control</th></tr></thead>
              <tbody>
                {s.rows.map(r => (
                  <tr key={r.key}>
                    <td className="small fw-semibold" style={{ minWidth: 190 }}>{r.label}</td>
                    <td style={{ minWidth: 170 }}>{input(`s9.${r.key}.status`, { kind: 'select', options: CONTROL_STATUSES })}{fromLms(`s9.${r.key}.status`)}</td>
                    <td style={{ minWidth: 240 }}>{input(`s9.${r.key}.position`, { kind: 'longtext' })}{fromLms(`s9.${r.key}.position`)}</td>
                    <td style={{ minWidth: 150 }}>{input(`s9.${r.key}.outcome`, { kind: 'select', options: CONTROL_OUTCOMES })}</td>
                    <td style={{ minWidth: 200 }}>{input(`s9.${r.key}.condition`, { kind: 'text' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <label htmlFor="ca-s9.retailValue" className="form-label small mb-1">9.1 Verified vehicle retail value {fromLms('s9.retailValue')}</label>
              {input('s9.retailValue', { kind: 'money' })}
            </div>
            <div className="col-md-4">
              <label htmlFor="ca-s9.existingExposure" className="form-label small mb-1">Tirhelo funded exposure already (incl. related borrowers) {fromLms('s9.existingExposure')}</label>
              {input('s9.existingExposure', { kind: 'money' })}
            </div>
          </div>
          <div className="bg-light rounded p-2 mt-3 small">
            <Calc label={`Recognised collateral value (${values['s9.recognisedPercent'] ?? 70}% of retail)`} value={recognisedValue(values)} />
            <Calc label="Tirhelo exposure after the proposed disbursement" value={exposureAfter(values)} />
            <Calc label="Security surplus / (shortfall)" value={securityCover(values)} signed />
          </div>
        </>
      )}
      {s.fields && (
        <div className="row g-2">
          {s.fields.map(f => (
            <div key={f.key} className={f.kind === 'longtext' ? 'col-12' : 'col-md-6'}>
              <label htmlFor={`ca-${s.key}.${f.key}`} className="form-label small mb-1">
                {f.label}{f.required && <span className="text-danger"> *</span>}{fromLms(`${s.key}.${f.key}`)}
              </label>
              {input(`${s.key}.${f.key}`, f)}
              {f.hint && <div className="form-text small">{f.hint}</div>}
            </div>
          ))}
        </div>
      )}
      {s.key === 's1' && <div className="bg-light rounded p-2 mt-3 small"><Calc label="Estimated Normal Monthly Cash Surplus / (Deficit)" value={cashSurplus(values)} signed /></div>}
      {s.key === 's2' && (
        <div className="bg-light rounded p-2 mt-3 small">
          <Calc label="Total Amount to Repay Tirhelo" value={totalToRepay(values)} />
          <Calc label="Expected Net Transaction Surplus / (Deficit)" value={netTransaction(values)} signed />
        </div>
      )}
      {s.outcome && outcomeButtons(`${s.key}.outcome`)}
    </div>
  );

  if (!applicationId) return null;
  const d = loanApplication?.loanDetails || {};
  const company = loanApplication?.company || {};
  const attentionCount = attention.missing.length + attention.failed.length + attention.hardStops.length + attention.issues.length;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h6 className="card-title mb-0">Business Loan Credit Assessment Report</h6>
          <small className="text-muted">
            Credit Assessment Template 27.10.2026.{' '}
            {report?.exists
              ? `Saved by ${report.updatedBy || 'someone'}${report.updatedAt ? ` · ${formatSouthAfricanDateTime(report.updatedAt)}` : ''}`
              : 'Not saved yet: filled in from the application.'}
          </small>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className={`badge ${RATING_CLASS[overall]}`}>Overall risk: {overall}</span>
          {decision && <span className="badge bg-primary">{decision}</span>}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(o => !o)} aria-expanded={open}>{open ? 'Hide report' : 'Open report'}</button>
          {report?.exists && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fillFromLms} title="Fill blank fields from the application, without changing anything typed">Fill blanks from LMS</button>}
          <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={save.isPending || isLoading || (!dirty && !!report?.exists)}>
            {save.isPending ? 'Saving…' : dirty || !report?.exists ? 'Save report' : 'Saved'}
          </button>
        </div>
      </div>
      {open && (
        <div className="card-body p-0">
          {/* Missing information, failed requirements and Hard Stops (guidelines, point 5) */}
          {attentionCount > 0 && (
            <div className="m-3 mb-0 d-grid gap-2 small">
              {attention.hardStops.length > 0 && (
                <div className="alert alert-dark mb-0"><strong>Hard Stop:</strong> {attention.hardStops.join(' · ')}</div>
              )}
              {attention.failed.length > 0 && (
                <div className="alert alert-danger mb-0"><strong>Failed:</strong> {attention.failed.join(' · ')}</div>
              )}
              {attention.issues.length > 0 && (
                <div className="alert alert-warning mb-0"><strong>Recommendation check:</strong><ul className="mb-0 ps-3">{attention.issues.map(i => <li key={i}>{i}</li>)}</ul></div>
              )}
              {attention.missing.length > 0 && (
                <details className="alert alert-light border mb-0">
                  <summary><strong>Missing information ({attention.missing.length})</strong></summary>
                  <ul className="mb-0 ps-3 mt-1">{attention.missing.map(m => <li key={m}>{m}</li>)}</ul>
                </details>
              )}
            </div>
          )}

          {/* The application, as captured: not captured again */}
          <div className="m-3 p-2 bg-light rounded small row g-2 mx-3">
            <div className="col-md-3"><span className="text-muted">Applicant</span><div className="fw-semibold">{company.companyName || company.registeredName || '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Registration no.</span><div className="fw-semibold">{company.companyRegNumber || company.registrationNumber || '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Funding type</span><div className="fw-semibold">{getFundingProduct(d.fundingType)?.shortName || d.fundingType || '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Offtaker / payer</span><div className="fw-semibold">{d.projectClientName || d.invoiceDebtorName || d.bridgePayerName || '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Amount requested</span><div className="fw-semibold">{money(d.loanAmount != null ? Number(d.loanAmount) : undefined)}</div></div>
            <div className="col-md-3"><span className="text-muted">Term</span><div className="fw-semibold">{d.numberOfInstallments ? `${d.numberOfInstallments} instalment(s)` : d.dueDate ? `Due ${d.dueDate}` : '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Credit assessor</span><div className="fw-semibold">{assessor || '—'}</div></div>
            <div className="col-md-3"><span className="text-muted">Application</span><div className="fw-semibold">{loanApplication?.applicationNumber || '—'}</div></div>
          </div>

          <div className="accordion accordion-flush">
            {SECTIONS.map(s => (
              <div className="accordion-item" key={s.key}>
                <h2 className="accordion-header">
                  <button type="button" className={`accordion-button py-2 ${isOpen(s.key) ? '' : 'collapsed'}`}
                    onClick={() => toggle(s.key)} aria-expanded={isOpen(s.key)}>
                    <span className="small fw-semibold">{s.title}</span>{sectionBadge(s)}
                  </button>
                </h2>
                {isOpen(s.key) && renderSection(s)}
              </div>
            ))}

            {/* 11. Overall risk: worked out from the sections, except Execution */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button type="button" className={`accordion-button py-2 ${isOpen('s11') ? '' : 'collapsed'}`}
                  onClick={() => toggle('s11')} aria-expanded={isOpen('s11')}>
                  <span className="small fw-semibold">11. Overall Risk Assessment</span><span className={`badge ms-2 ${RATING_CLASS[overall]}`}>{overall}</span>
                </button>
              </h2>
              {isOpen('s11') && (
                <div className="p-3">
                  <p className="small text-muted fst-italic mb-3">Summarises the sections above; not a second score (manual 10). Execution has no section in the template, so it is rated here.</p>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="small text-muted"><tr><th>Risk Area</th><th>Risk Rating</th><th>Key Finding / Mitigation</th></tr></thead>
                      <tbody>
                        {RISK_AREAS.map(a => (
                          <tr key={a.key}>
                            <td className="small fw-semibold" style={{ minWidth: 200 }}>{a.label}</td>
                            <td style={{ minWidth: 150 }}>
                              {a.key === 'execution'
                                ? input('s11.execution', { kind: 'select', options: RATINGS })
                                : <span className={`badge ${RATING_CLASS[areaRating(values, a)]}`}>{areaRating(values, a)}</span>}
                            </td>
                            <td className="small">{a.key === 'execution' ? input('s11.executionFinding', { kind: 'text' }) : (areaFindings(values, a) || <span className="text-muted">—</span>)}</td>
                          </tr>
                        ))}
                        <tr className="table-light">
                          <td className="small fw-bold">Overall Risk Rating</td>
                          <td><span className={`badge ${RATING_CLASS[overall]}`}>{overall}</span></td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* 12. Credit recommendation: the assessor's, approval stays with Credit Committee */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button type="button" className={`accordion-button py-2 ${isOpen('s12') ? '' : 'collapsed'}`}
                  onClick={() => toggle('s12')} aria-expanded={isOpen('s12')}>
                  <span className="small fw-semibold">12. Credit Recommendation</span>{decision && <span className="badge bg-primary ms-2">{decision}</span>}
                </button>
              </h2>
              {isOpen('s12') && (
                <div className="p-3">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label htmlFor="ca-s12.decision" className="form-label small mb-1 fw-semibold">12.1 Final Recommendation ↓</label>
                      {input('s12.decision', { kind: 'select', options: DECISIONS })}
                    </div>
                    <div className="col-12">
                      <label htmlFor="ca-s12.rationale" className="form-label small mb-1 fw-semibold">Decision Rationale <span className="text-danger">*</span></label>
                      {input('s12.rationale', { kind: 'longtext' })}
                    </div>
                  </div>
                  <p className="small text-muted mt-2 mb-0">
                    This is the assessor's recommendation. It is submitted with the assessment below, and Credit Committee makes the approval decision.
                  </p>
                </div>
              )}
            </div>
          </div>

          {dirty && (
            <div className="d-flex justify-content-end p-3 border-top">
              <button type="button" className="btn btn-primary" onClick={onSave} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save report'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditAssessmentTemplateReport;
